import { useReducer } from 'react';
import type { FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Feedback } from '../components/Feedback';
import { PasswordInput } from '../components/PasswordInput';
import { authLink, getReturnTo } from '../lib/authNavigation';

interface State {
  email: string;
  password: string;
  isLoading: boolean;
  error: string | null;
}

type Action =
  | { type: 'FIELD'; field: 'email' | 'password'; value: string }
  | { type: 'SUBMIT' }
  | { type: 'ERROR'; error: string }
  | { type: 'DONE' };

const initial: State = { email: '', password: '', isLoading: false, error: null };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'FIELD':
      return { ...state, [action.field]: action.value, error: null };
    case 'SUBMIT':
      return { ...state, isLoading: true, error: null };
    case 'ERROR':
      return { ...state, isLoading: false, error: action.error };
    case 'DONE':
      return { ...state, isLoading: false };
  }
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Route: `/login`. Email + password sign-in; navigates to `/dashboard` on success. */
export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = getReturnTo(location.search);
  const [state, dispatch] = useReducer(reducer, initial);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!EMAIL_RE.test(state.email)) {
      dispatch({ type: 'ERROR', error: 'enter a valid email address' });
      return;
    }
    if (!state.password) {
      dispatch({ type: 'ERROR', error: 'password is required' });
      return;
    }
    dispatch({ type: 'SUBMIT' });
    try {
      await login(state.email, state.password);
      navigate(returnTo, { replace: true });
    } catch (err) {
      dispatch({ type: 'ERROR', error: err instanceof Error ? err.message : 'login failed' });
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <p className="text-muted" style={{ marginBottom: 16 }}>{returnTo.startsWith('/docs') ? 'Sign in to access the standards.' : 'sign in to your account'}</p>

      {state.error && <Feedback type="error" message={state.error} />}

      <div className="form-row">
        <label className="label" htmlFor="login-email">email</label>
        <input
          type="email"
          autoComplete="email"
          id="login-email"
          value={state.email}
          onChange={(e) => dispatch({ type: 'FIELD', field: 'email', value: e.target.value })}
          className="form-input"
        />
      </div>

      <div className="form-row">
        <label className="label" htmlFor="login-password">password</label>
        <PasswordInput
          autoComplete="current-password"
          id="login-password"
          value={state.password}
          onChange={(e) => dispatch({ type: 'FIELD', field: 'password', value: e.target.value })}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <button type="submit" className="btn btn-primary" disabled={state.isLoading}>
          {state.isLoading ? 'signing in...' : 'sign in'}
        </button>
      </div>

      <p className="text-muted" style={{ marginTop: 24 }}>
        new here? <Link to={authLink('/signup', returnTo)}>→ create account</Link>
      </p>
    </form>
  );
}
